import React, { useRef, useEffect, useState, useCallback } from 'react';

/**
 * ScrollableTable: Envuelve una tabla y agrega un scrollbar horizontal 
 * fijo en la parte superior, sincronizado con el scroll real de la tabla.
 * Solo se muestra cuando la tabla es más ancha que su contenedor.
 */
const ScrollableTable = ({ children }) => {
  const tableContainerRef = useRef(null);
  const topScrollRef = useRef(null);
  const [scrollWidth, setScrollWidth] = useState(0);
  const [showTopScroll, setShowTopScroll] = useState(false);
  const syncing = useRef(false);

  const checkOverflow = useCallback(() => {
    const el = tableContainerRef.current;
    if (!el) return;
    const tableEl = el.querySelector('table');
    const realWidth = tableEl ? tableEl.scrollWidth : el.scrollWidth;
    const isOverflowing = realWidth > el.clientWidth + 2;
    setScrollWidth(realWidth);
    setShowTopScroll(isOverflowing);
  }, []);

  useEffect(() => {
    checkOverflow();
    window.addEventListener('resize', checkOverflow);
    // Observer para detectar cambios en el contenido de la tabla
    const observer = new ResizeObserver(checkOverflow);
    if (tableContainerRef.current) observer.observe(tableContainerRef.current);
    return () => {
      window.removeEventListener('resize', checkOverflow);
      observer.disconnect();
    };
  }, [checkOverflow, children]);

  const handleTopScroll = () => {
    if (syncing.current) return;
    syncing.current = true;
    if (tableContainerRef.current && topScrollRef.current) {
      tableContainerRef.current.scrollLeft = topScrollRef.current.scrollLeft;
    }
    requestAnimationFrame(() => { syncing.current = false; });
  };

  const handleTableScroll = () => {
    if (syncing.current) return;
    syncing.current = true;
    if (topScrollRef.current && tableContainerRef.current) {
      topScrollRef.current.scrollLeft = tableContainerRef.current.scrollLeft;
    }
    requestAnimationFrame(() => { syncing.current = false; });
  };

  return (
    <div className="scrollable-table-wrapper">
      {/* Scrollbar superior — solo visible cuando la tabla desborda */}
      {showTopScroll && (
        <div
          ref={topScrollRef}
          onScroll={handleTopScroll}
          className="top-scrollbar"
        >
          <div style={{ width: `${scrollWidth}px`, height: '1px' }} />
        </div>
      )}

      {/* Contenedor real de la tabla */}
      <div
        ref={tableContainerRef}
        onScroll={handleTableScroll}
        className="table-container"
      >
        {children}
      </div>
    </div>
  );
};

export default ScrollableTable;
